import { getQueryKey } from '@trpc/react-query'
import { queryClient } from '../lib/query-client'
import { trpc } from '../lib/trpc'
import { useTerminalStore } from '../stores/terminal.store'
import { useTasksStore } from '../stores/tasks.store'
import type { TerminalEventWithSeq } from '../../../../shared/types/terminal-event.js'
import { dbgEvent } from '../lib/debug'

export const terminalHandler = {
  onTerminalEvent(sessionId: string, data: TerminalEventWithSeq): void {
    dbgEvent('Zustand', 'terminal.onTerminalEvent', { sessionId: sessionId.slice(0, 8), type: data.type })
    // Le terminal reste inchange : chaque event (y compris task_*) entre dans liveEvents comme
    // avant — c'est le regime A, l'histoire de la conversation (« Ce qui ne change PAS », brief §4bis).
    useTerminalStore.getState().appendLiveEvent(sessionId, data)

    // EN PLUS : le terminalHandler est l'ecrivain DEBUT/FIN du tasks.store (brief §4bis etage 6).
    // Les variantes de tache enrichies (regime A) portent l'ouverture et la cloture d'une tache ;
    // les progressions (regime B) passent, elles, par tasksHandler. Deux ecrivains, un seul store.
    const tasks = useTasksStore.getState()
    if (data.type === 'system_init') {
      // Reset des taches de la session au (re)demarrage du worker : background_tasks est
      // per-worker et se vide au (re)demarrage du CLI. Sans ce reset, un indicateur d'une
      // generation precedente resterait coince (reconception suivi #31513, cycle de vie point 6).
      tasks.resetSession(sessionId)
    } else if (data.type === 'task_start') {
      tasks.startTask(sessionId, data.taskId, {
        description: data.description,
        subagent_type: data.subagent_type,
        task_type: data.task_type,
      })
    } else if (data.type === 'task_notification') {
      tasks.notifyTask(sessionId, data.taskId, {
        status: data.status,
        summary: data.summary,
        usage: data.usage,
      })
    } else if (data.type === 'task_updated') {
      tasks.updateTask(sessionId, data.taskId, {
        status: data.status,
        is_backgrounded: data.is_backgrounded,
        error: data.error,
        end_time: data.end_time,
      })
    }
  },

  /**
   * Appelé par le dispatch quand status:changed est reçu avec status idle|error|closed.
   *
   * Fix race reconciliation TERM (debug-vue-terminale-reconstruction-pulsar-2026-08-10) :
   * ne purge PLUS liveEvents en synchrone à la réception du statut. `invalidateQueries` sur
   * terminalEvents.listBySession déclenche/rejoint le refetch de l'historique BDD (TanStack
   * Query v5 dédup une invalidation concurrente sur la même queryKey avec celle de
   * messages.handler.ts:onComplete) et sa promesse ne se résout qu'une fois ce refetch actif
   * terminé. C'est seulement à ce moment-là — jamais avant — qu'on lit les seq confirmés en
   * BDD et qu'on les retire de liveEvents (pruneConfirmedLiveEvents, terminal.store.ts).
   *
   * Ni un wipe total ni un wipe simplement différé : le prune ne retire QUE ce qui est prouvé
   * persisté, donc même un tour N+1 démarré (nouveaux appendLiveEvent) pendant qu'on attend la
   * confirmation du tour N n'est jamais effacé par erreur.
   *
   * .finally() et non .then() : si le refetch échoue (réseau), on prune quand même avec un
   * Set vide -> aucun retrait -> aucune perte, liveEvents reste peuplé un peu plus longtemps
   * (dégradation propre, jamais une nouvelle fenêtre de trou visuel).
   *
   * Dépendance intentionnelle via dispatch (pas via status.store) — isolation des stores.
   */
  onStatusReset(sessionId: string, data: { status: string }): void {
    const resetStatuses: string[] = ['idle', 'error', 'closed']
    if (!resetStatuses.includes(data.status)) return

    const terminalKey = getQueryKey(trpc.messages.terminalEvents.listBySession, { sessionId }, 'query')
    void queryClient.invalidateQueries({ queryKey: terminalKey }).finally(() => {
      const rows = queryClient.getQueryData<Array<{ seq: number | string | null }>>(terminalKey) ?? []
      const confirmedSeqs = new Set<number>()
      for (const row of rows) {
        if (row.seq !== null && row.seq !== undefined) confirmedSeqs.add(Number(row.seq))
      }
      useTerminalStore.getState().pruneConfirmedLiveEvents(sessionId, confirmedSeqs)
    })
  },
}
